import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Save, Download, FileText, Sparkles, AlertTriangle, Info, ShieldAlert, Target, TrendingUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OUTCOME_OPTIONS, outcomeLabel, outcomeTone } from "@/lib/outcomes";
import { ClinicalReviewPanel } from "@/components/ClinicalReviewPanel";

const DEFAULT_INPUTS = {
  failed_euploid: 2, failed_untested: 0,
  no_pgta: false, high_sdf: false, male_karyo: false, severe_oat: false,
  era_displaced: false, ce_cd138: false, endo_tb: false, microbiome: false, anatomy: false,
  th1_th2: false, asri_nk: false, kir_mismatch: false, hla_antibodies: false, hla_crossmatch: false,
  coeliac: false, chronic_inflammation: false, hyperhomocysteinemia: false, vit_d_deficiency: false,
  thyroid_autoimm: false, insulin_resistance: false, hyperprolactinemia: false,
  inherited_thrombophilia: false, endometriosis_occult: false, seminal_ros: false,
};

const STEPS = [
  { key: "baseline", label: "Baseline profile" },
  { key: "embryo", label: "Embryo & sperm" },
  { key: "uterine", label: "Uterus & endometrium" },
  { key: "immuno", label: "Immunology & HLA" },
  { key: "systemic", label: "Rare & systemic" },
  { key: "plan", label: "Generated plan" },
];

export default function Workflow() {
  const { caseId } = useParams();
  const nav = useNavigate();
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [notes, setNotes] = useState("");
  const [referringDoctor, setReferringDoctor] = useState("");
  const [label, setLabel] = useState("");
  const [protocol, setProtocol] = useState(null);
  const [step, setStep] = useState(0);
  const [savedId, setSavedId] = useState(caseId || null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState("pending");
  const [outcomeDate, setOutcomeDate] = useState("");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [reviewData, setReviewData] = useState({ review: null, status: null, extended: null });

  useEffect(() => {
    api.get("/protocol/factor-suggestions")
      .then((r) => setSuggestions(r.data))
      .catch(() => setSuggestions(null));
    // Runs once on mount; `api` is a stable singleton and setSuggestions is a stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!caseId) return;
    api.get(`/cases/${caseId}`).then(({ data }) => {
      setInputs({ ...DEFAULT_INPUTS, ...data.inputs });
      setLabel(data.patient_label);
      setNotes(data.notes || "");
      setReferringDoctor(data.referring_doctor || "");
      setProtocol(data.protocol);
      setSavedId(data.id);
      setOutcome(data.outcome || "pending");
      setOutcomeDate(data.outcome_date || "");
      setOutcomeNote(data.outcome_note || "");
      setReviewData({
        review: data.clinical_review || null,
        status: data.clinical_review_status || (data.clinical_review ? "ready" : null),
        extended: data.extended || null,
      });
      setStep(5);
    }).catch((e) => toast.error(formatApiError(e)));
    // Only re-fetch when the route param changes; setters and `api` are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const patch = (k, v) => setInputs((p) => ({ ...p, [k]: v }));

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/protocol/generate", inputs);
      setProtocol(data);
      setStep(5);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!label.trim()) { toast.error("Please add a patient label first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/cases", { patient_label: label, notes, referring_doctor: referringDoctor || null, inputs });
      setSavedId(data.id);
      toast.success("Case saved to your history");
      nav(`/workflow/${data.id}`, { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!savedId) { toast.error("Save the case first to export PDF"); return; }
    try {
      const res = await api.get(`/cases/${savedId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `RIF_plan_${label.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8 reveal">
      <aside className="lg:col-span-3">
        <div className="eyebrow">RIF Workflow</div>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">5-step protocol</h1>
        <p className="mt-2 text-sm text-slate-600">Move step by step. The generator uses your inputs to compose a dosed plan.</p>

        <ol className="mt-6 space-y-1">
          {STEPS.map((s, i) => (
            <li key={s.key}>
              <button
                onClick={() => setStep(i)}
                className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  step === i ? "bg-blue-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
                data-testid={`step-tab-${i}`}>
                <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-bold ${step === i ? "bg-white text-blue-900" : "bg-slate-200 text-slate-700"}`}>
                  {i + 1}
                </span>
                {s.label}
              </button>
            </li>
          ))}
        </ol>
      </aside>

      <section className="lg:col-span-9 min-w-0">
        {suggestions && suggestions.suggestions && suggestions.suggestions.length > 0 && (
          <SuggestionsCard data={suggestions} activeInputs={inputs} />
        )}
        {savedId && (
          <OutcomeCard
            caseId={savedId}
            outcome={outcome}
            outcomeDate={outcomeDate}
            outcomeNote={outcomeNote}
            onSaved={(d) => {
              setOutcome(d.outcome);
              setOutcomeDate(d.outcome_date || "");
              setOutcomeNote(d.outcome_note || "");
            }}
          />
        )}
        <div className="border border-slate-200 bg-white rounded-md p-6">
          {step === 0 && <Baseline inputs={inputs} patch={patch} label={label} setLabel={setLabel} notes={notes} setNotes={setNotes} referringDoctor={referringDoctor} setReferringDoctor={setReferringDoctor} />}
          {step === 1 && <EmbryoStep inputs={inputs} patch={patch} />}
          {step === 2 && <UterineStep inputs={inputs} patch={patch} />}
          {step === 3 && <ImmunoStep inputs={inputs} patch={patch} />}
          {step === 4 && <SystemicStep inputs={inputs} patch={patch} />}
          {step === 5 && <PlanStep protocol={protocol} onGenerate={generate} busy={busy} />}

          <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-5">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="step-prev-btn">Back</Button>
            <div className="flex items-center gap-2">
              {step < 5 && (
                <Button
                  onClick={() => (step === 4 ? generate() : setStep((s) => s + 1))}
                  disabled={busy}
                  className="bg-blue-900 hover:bg-blue-800"
                  data-testid="step-next-btn">
                  {step === 4 ? (busy ? "Generating…" : "Generate protocol") : "Next"}
                </Button>
              )}
              {step === 5 && protocol && (
                <>
                  <Button variant="outline" onClick={save} disabled={busy} data-testid="save-case-btn">
                    <Save className="h-4 w-4 mr-2" strokeWidth={1.75} /> {savedId ? "Save as new case" : "Save case"}
                  </Button>
                  <Button className="bg-blue-900 hover:bg-blue-800" onClick={downloadPdf} disabled={!savedId} data-testid="download-pdf-btn">
                    <Download className="h-4 w-4 mr-2" strokeWidth={1.75} /> Download PDF
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {step === 5 && protocol && savedId && (
          <ClinicalReviewPanel
            caseId={savedId}
            endpointBase="/cases"
            module="rif"
            initialReview={reviewData.review}
            initialStatus={reviewData.status}
            initialExtended={reviewData.extended}
            onReviewReady={(rv) => setReviewData((s) => ({ ...s, review: rv, status: "ready" }))}
          />
        )}
      </section>
    </div>
  );
}

function SuggestionsCard({ data, activeInputs }) {
  const active = new Set(Object.entries(activeInputs).filter(([k, v]) => typeof v === "boolean" && v).map(([k]) => k));
  return (
    <div className="mb-4 border-l-4 border-teal-700 bg-teal-50 rounded-sm p-4" data-testid="suggestions-card">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-teal-700" strokeWidth={1.75} />
        <div className="eyebrow text-teal-800">Historically best-outcome factors in your cohort</div>
      </div>
      <p className="mt-1 text-xs text-slate-600">
        Amongst {data.tracked_cases} case{data.tracked_cases === 1 ? "" : "s"} with a recorded outcome, addressing these factors correlated with the highest success rate (min {data.min_n} cases per factor).
      </p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="suggestions-list">
        {data.suggestions.map((s) => (
          <div
            key={s.key}
            className={`text-xs rounded-sm border px-2.5 py-1.5 flex items-center gap-2 ${active.has(s.key) ? "border-teal-700 bg-white text-slate-900" : "border-slate-300 bg-white text-slate-700"}`}
            title={`${s.success_count}/${s.n} successful cycles`}
            data-testid={`suggestion-${s.key}`}
          >
            <span className="font-semibold">{s.label}</span>
            <span className="font-mono text-teal-800 font-bold">{s.success_rate}%</span>
            {active.has(s.key) && <span className="text-[10px] uppercase tracking-widest text-teal-700">On</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function OutcomeCard({ caseId, outcome, outcomeDate, outcomeNote, onSaved }) {
  const [val, setVal] = useState(outcome);
  const [dt, setDt] = useState(outcomeDate);
  const [nt, setNt] = useState(outcomeNote);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setVal(outcome); setDt(outcomeDate); setNt(outcomeNote); }, [outcome, outcomeDate, outcomeNote]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/cases/${caseId}/outcome`, {
        outcome: val,
        outcome_date: dt.trim() || null,
        outcome_note: nt.trim() || null,
      });
      onSaved(data);
      toast.success("Outcome updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb-4 border border-slate-200 bg-white rounded-md p-5 flex flex-col md:flex-row md:items-end gap-4">
      <div className="flex items-center gap-3 shrink-0">
        <Target className="h-5 w-5 text-blue-900" strokeWidth={1.75} />
        <div>
          <div className="eyebrow">Cycle outcome</div>
          <div className={`mt-1 text-xs uppercase tracking-widest font-bold px-2 py-1 rounded-sm border inline-block ${outcomeTone(outcome)}`} data-testid="outcome-badge">
            {outcomeLabel(outcome)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 flex-1 min-w-0">
        <div className="sm:col-span-2">
          <Label htmlFor="oc-sel">Outcome</Label>
          <Select value={val} onValueChange={setVal}>
            <SelectTrigger id="oc-sel" data-testid="outcome-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {OUTCOME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`outcome-opt-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="oc-dt">Date</Label>
          <Input id="oc-dt" type="date" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="outcome-date" />
        </div>
        <div className="flex items-end">
          <Button onClick={save} disabled={saving} className="w-full bg-blue-900 hover:bg-blue-800" data-testid="outcome-save-btn">
            {saving ? "Saving…" : "Save outcome"}
          </Button>
        </div>
        <div className="sm:col-span-4">
          <Label htmlFor="oc-nt">Note (optional)</Label>
          <Input id="oc-nt" value={nt} onChange={(e) => setNt(e.target.value)} placeholder="Gestational age, complications, medications used…" data-testid="outcome-note" />
        </div>
      </div>
    </div>
  );
}

function StepHeader({ title, description }) {
  return (
    <div>
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-600">{description}</p>
    </div>
  );
}

function CheckRow({ id, label, description, checked, onChange, testId }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 border border-slate-200 rounded-sm p-3 cursor-pointer hover:border-blue-900 transition-colors">
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
      </div>
    </label>
  );
}

function Baseline({ inputs, patch, label, setLabel, notes, setNotes, referringDoctor, setReferringDoctor }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 1 · Baseline & profile" description="Define the patient and quantify prior implantation attempts." />
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="patient-label">Patient label (initials or code)</Label>
          <Input id="patient-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. R.K. — 34y" data-testid="input-patient-label" />
        </div>
        <div>
          <Label htmlFor="ref-doc">Referring doctor / clinic (optional)</Label>
          <Input id="ref-doc" maxLength={160} value={referringDoctor} onChange={(e) => setReferringDoctor(e.target.value)} placeholder="Dr. Name / Clinic" data-testid="input-referring-doctor" />
        </div>
        <div>
          <Label htmlFor="fe">Failed EUPLOID (PGT-A) transfers</Label>
          <Input id="fe" type="number" min={0} max={20} value={inputs.failed_euploid}
            onChange={(e) => patch("failed_euploid", Number(e.target.value || 0))} data-testid="input-failed-euploid" />
        </div>
        <div>
          <Label htmlFor="fu">Failed UNTESTED transfers</Label>
          <Input id="fu" type="number" min={0} max={20} value={inputs.failed_untested}
            onChange={(e) => patch("failed_untested", Number(e.target.value || 0))} data-testid="input-failed-untested" />
        </div>
      </div>
      <div>
        <Label htmlFor="notes">Clinical notes (optional)</Label>
        <Textarea id="notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="AMH, comorbidities, previous stimulation…" data-testid="input-notes" />
      </div>
      <div className="border-l-4 border-slate-300 bg-slate-50 p-3 rounded-sm text-xs text-slate-600">
        <b>RIF definition:</b> failure to achieve clinical pregnancy after transfer of ≥3 good-quality cleavage/blastocyst embryos, or ≥2 euploid blastocysts.
      </div>
    </div>
  );
}

function EmbryoStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 2 · Embryonic & male factor" description="Genetic status of embryos and sperm DNA integrity." />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="no_pgta" label="Embryos NOT tested for aneuploidy" description="No PGT-A performed" checked={inputs.no_pgta} onChange={(v) => patch("no_pgta", !!v)} testId="chk-no-pgta" />
        <CheckRow id="high_sdf" label="High Sperm DNA Fragmentation" description="SDF > 30%" checked={inputs.high_sdf} onChange={(v) => patch("high_sdf", !!v)} testId="chk-high-sdf" />
        <CheckRow id="male_karyo" label="Abnormal paternal karyotype" description="e.g. balanced translocation" checked={inputs.male_karyo} onChange={(v) => patch("male_karyo", !!v)} testId="chk-male-karyo" />
        <CheckRow id="severe_oat" label="Severe OAT / poor morphokinetics" checked={inputs.severe_oat} onChange={(v) => patch("severe_oat", !!v)} testId="chk-severe-oat" />
      </div>
    </div>
  );
}

function UterineStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 3 · Uterine & endometrial workup" description="Cavity, receptivity, infection and microbiome." />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="era" label="Displaced window of implantation" description="Non-receptive ERA / pre- or post-receptive" checked={inputs.era_displaced} onChange={(v) => patch("era_displaced", !!v)} testId="chk-era" />
        <CheckRow id="ce" label="Chronic endometritis" description="CD138+ plasma cells" checked={inputs.ce_cd138} onChange={(v) => patch("ce_cd138", !!v)} testId="chk-ce" />
        <CheckRow id="tb" label="Latent endometrial TB" description="PCR / GeneXpert / AFB positive" checked={inputs.endo_tb} onChange={(v) => patch("endo_tb", !!v)} testId="chk-tb" />
        <CheckRow id="mb" label="Endometrial dysbiosis" description="Low Lactobacillus or pathogens" checked={inputs.microbiome} onChange={(v) => patch("microbiome", !!v)} testId="chk-microbiome" />
        <CheckRow id="anat" label="Cavity anomaly / adenomyosis" description="Septum, polyps, adenomyosis" checked={inputs.anatomy} onChange={(v) => patch("anatomy", !!v)} testId="chk-anatomy" />
      </div>
    </div>
  );
}

function ImmunoStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader title="Step 4 · Immunology, HLA & alloimmune" description="Investigate when embryos are euploid and cavity is normal." />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="th12" label="Th1/Th2 imbalance" description="High TNF-α ratio, chronic inflammation" checked={inputs.th1_th2} onChange={(v) => patch("th1_th2", !!v)} testId="chk-th12" />
        <CheckRow id="asri" label="Elevated NK cells / ANA / APA" description="Peripheral or uterine NK, autoantibodies" checked={inputs.asri_nk} onChange={(v) => patch("asri_nk", !!v)} testId="chk-asri" />
        <CheckRow id="kir" label="High-risk KIR genotype" description="Maternal KIR AA vs paternal HLA-C2" checked={inputs.kir_mismatch} onChange={(v) => patch("kir_mismatch", !!v)} testId="chk-kir" />
        <CheckRow id="dsa" label="Positive HLA antibodies / DSA" checked={inputs.hla_antibodies} onChange={(v) => patch("hla_antibodies", !!v)} testId="chk-dsa" />
        <CheckRow id="cross" label="Positive paternal HLA cross-match" checked={inputs.hla_crossmatch} onChange={(v) => patch("hla_crossmatch", !!v)} testId="chk-crossmatch" />
      </div>
    </div>
  );
}

function SystemicStep({ inputs, patch }) {
  return (
    <div className="space-y-6">
      <StepHeader
        title="Step 5 · Rare & systemic causes"
        description="Often missed in unexplained RIF — screen once, treat if positive."
      />
      <div className="grid sm:grid-cols-2 gap-3">
        <CheckRow id="coeliac" label="Coeliac disease" description="tTG-IgA / anti-EMA positive" checked={inputs.coeliac} onChange={(v) => patch("coeliac", !!v)} testId="chk-coeliac" />
        <CheckRow id="crp" label="Chronic low-grade inflammation" description="hs-CRP > 5 mg/L or elevated ESR" checked={inputs.chronic_inflammation} onChange={(v) => patch("chronic_inflammation", !!v)} testId="chk-crp" />
        <CheckRow id="hcy" label="Hyperhomocysteinemia (couple)" description="Screen BOTH partners + MTHFR" checked={inputs.hyperhomocysteinemia} onChange={(v) => patch("hyperhomocysteinemia", !!v)} testId="chk-hcy" />
        <CheckRow id="vitd" label="Vitamin D deficiency" description="25-OH-D < 20 ng/mL" checked={inputs.vit_d_deficiency} onChange={(v) => patch("vit_d_deficiency", !!v)} testId="chk-vitd" />
        <CheckRow id="thyroid" label="Thyroid autoimmunity / subclinical hypo" description="Anti-TPO + or TSH > 2.5" checked={inputs.thyroid_autoimm} onChange={(v) => patch("thyroid_autoimm", !!v)} testId="chk-thyroid" />
        <CheckRow id="insulin" label="Insulin resistance / metabolic" description="HOMA-IR ≥ 2.5 or HbA1c ≥ 5.7" checked={inputs.insulin_resistance} onChange={(v) => patch("insulin_resistance", !!v)} testId="chk-insulin" />
        <CheckRow id="prl" label="Hyperprolactinemia" checked={inputs.hyperprolactinemia} onChange={(v) => patch("hyperprolactinemia", !!v)} testId="chk-prl" />
        <CheckRow id="thromb" label="Inherited thrombophilia" description="FVL / Prothrombin G20210A / PC/PS/AT" checked={inputs.inherited_thrombophilia} onChange={(v) => patch("inherited_thrombophilia", !!v)} testId="chk-thromb" />
        <CheckRow id="endo" label="Occult / deep endometriosis" checked={inputs.endometriosis_occult} onChange={(v) => patch("endometriosis_occult", !!v)} testId="chk-endo" />
        <CheckRow id="ros" label="Seminal oxidative stress (ROS)" description="Elevated seminal ROS or DFI" checked={inputs.seminal_ros} onChange={(v) => patch("seminal_ros", !!v)} testId="chk-ros" />
      </div>
    </div>
  );
}

function severityStyle(sev) {
  if (sev === "critical") return { cls: "severity-critical", icon: ShieldAlert, color: "text-red-700" };
  if (sev === "warning") return { cls: "severity-warning", icon: AlertTriangle, color: "text-amber-700" };
  return { cls: "severity-info", icon: Info, color: "text-teal-700" };
}

function PlanStep({ protocol, onGenerate, busy }) {
  if (!protocol) {
    return (
      <div className="text-center py-10">
        <Sparkles className="h-8 w-8 mx-auto text-blue-900" strokeWidth={1.5} />
        <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">Generate the RIF protocol</h2>
        <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
          Click below to compose a dosed, evidence-graded management plan for this patient.
        </p>
        <Button onClick={onGenerate} disabled={busy} className="mt-6 h-11 px-6 bg-blue-900 hover:bg-blue-800" data-testid="generate-protocol-btn">
          {busy ? "Generating…" : "Generate protocol"}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="generated-plan">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-900" strokeWidth={1.75} /> Management plan
        </h2>
        <p className="mt-1 text-sm text-slate-600">{protocol.diagnosis_summary}</p>
      </div>
      {protocol.sections.map((sec) => (
        <div key={sec.key} className="space-y-3">
          <div className="eyebrow">{sec.title}</div>
          {sec.items.length === 0 && <div className="text-sm text-slate-500 italic">No targeted findings in this domain.</div>}
          {sec.items.map((it) => {
            const s = severityStyle(it.severity);
            const Icon = s.icon;
            return (
              <div key={it.key} className={`border-l-4 rounded-sm p-4 ${s.cls}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${s.color}`} strokeWidth={1.75} />
                  <div className="font-semibold text-slate-900">{it.title}</div>
                </div>
                <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-6">
                  {it.lines.map((l, i) => <li key={`${it.key}-line-${i}`}>{l}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
